import type { MaybeRefOrGetter } from 'vue';
import { useEventListener } from '@vueuse/core';
import { onBeforeUnmount, onMounted, onUpdated, ref, toValue } from 'vue';

export function useAutoWidthInput(el: MaybeRefOrGetter<HTMLInputElement | null>) {
	const shadowElement = ref<HTMLElement | null>(null);

	onMounted(() => {
		const inputEl = toValue(el);
		if (!inputEl) return;

		if (CSS.supports('field-sizing', 'content')) {
			// the future is now!
			inputEl.style.setProperty('field-sizing', 'content');
			return;
		}

		shadowElement.value = document.createElement('div');
		shadowElement.value.setAttribute('aria-hidden', 'true');
		copyStyles();
		document.body.append(shadowElement.value);

		useEventListener(inputEl, 'input', updateWidth);
		updateWidth();
	});

	onUpdated(updateWidth);

	onBeforeUnmount(() => {
		shadowElement.value?.remove();
	});

	function updateWidth() {
		const inputEl = toValue(el);
		if (!inputEl || !shadowElement.value) return;

		const value = inputEl.value || inputEl.placeholder;
		shadowElement.value.textContent = value ?? '';

		inputEl.style.width = `${shadowElement.value.scrollWidth}px`;
	}

	function copyStyles() {
		const inputEl = toValue(el);
		if (!inputEl || !shadowElement.value) return;

		const styles = window.getComputedStyle(inputEl);

		Object.assign(shadowElement.value.style, {
			position: 'absolute',
			top: '0',
			left: '0',
			visibility: 'hidden',
			height: '0',
			overflow: 'hidden',
			whiteSpace: 'pre',
			fontSize: styles.fontSize,
			fontFamily: styles.fontFamily,
			fontWeight: styles.fontWeight,
			fontStyle: styles.fontStyle,
			letterSpacing: styles.letterSpacing,
			textTransform: styles.textTransform,
			paddingRight: `calc(${styles.paddingRight} + ${styles.borderRightWidth})`,
			paddingLeft: `calc(${styles.paddingLeft} + ${styles.borderLeftWidth})`,
		});
	}
}
